// ── Replay Renderer ──
// Applies replay frames to the Three.js scene, driving remote players,
// grenades, bomb state, and events through existing game systems.

import * as THREE from 'three';
import type { WeaponId } from '../core/config';
import { type FPSCamera } from '../renderer/camera';
import { type SpectatorSystem } from '../spectator/spectator';
import { RemotePlayer } from '../gameplay/player';
import { type ReplayFrame } from './replay-recorder';
import { type PlayerSnapshot } from '../net/messages';
import { skinRegistry } from '../cosmetics/skin-registry';
import { type SkinLoader } from '../cosmetics/skin-loader';

export class ReplayRenderer {
  private scene: THREE.Scene;
  private camera: FPSCamera;
  private spectator: SpectatorSystem;
  private skinLoader: SkinLoader;

  // Managed replay entities
  private replayPlayers = new Map<number, RemotePlayer>();
  private followTargetId = 0;

  constructor(
    scene: THREE.Scene,
    camera: FPSCamera,
    spectator: SpectatorSystem,
    skinLoader: SkinLoader,
  ) {
    this.scene = scene;
    this.camera = camera;
    this.spectator = spectator;
    this.skinLoader = skinLoader;
  }

  /** Set which player the camera should follow. */
  setFollowTarget(playerId: number): void {
    this.followTargetId = playerId;
    if (this.spectator.active) {
      this.spectator.setTarget(playerId);
    }
  }

  /** Get current follow target. */
  getFollowTarget(): number {
    return this.followTargetId;
  }

  /** Get all replay players for spectator system. */
  getPlayers(): Map<number, RemotePlayer> {
    return this.replayPlayers;
  }

  /**
   * Apply a replay frame to the scene. Updates all player positions,
   * grenade states, bomb state, and processes events.
   */
  applyFrame(frame: ReplayFrame, prevFrame: ReplayFrame | null): void {
    this.updatePlayers(frame);
    this.updateSpectatorCamera(frame);
  }

  /** Clean up all replay-created entities from the scene. */
  dispose(): void {
    for (const [id, player] of this.replayPlayers) {
      player.removeMesh(this.scene);
    }
    this.replayPlayers.clear();
    this.followTargetId = 0;
  }

  // ── Internal ──

  private updatePlayers(frame: ReplayFrame): void {
    const seenIds = new Set<number>();

    for (const [id, snapshot] of frame.players) {
      seenIds.add(id);

      let remote = this.replayPlayers.get(id);
      if (!remote) {
        remote = new RemotePlayer();
        remote.id = id;
        remote.name = `Player ${id}`;

        const skinId = snapshot.skinId ?? 0;
        const skinDef = skinRegistry.resolveAny(skinId);
        remote.createFromSkin(this.scene, skinDef.id, this.skinLoader, skinDef.fallbackColor);

        this.replayPlayers.set(id, remote);
      }

      const weaponSkinId = snapshot.weaponSkinId ?? 0;
      if (snapshot.weapon && (snapshot.weapon !== remote.currentWeapon || weaponSkinId !== remote.currentWeaponSkinId)) {
        remote.equipWeapon(snapshot.weapon as WeaponId, weaponSkinId);
      }

      // Update position and state
      remote.health = snapshot.health;
      remote.isCrouching = snapshot.isCrouching ?? false;
      remote.isSliding = (snapshot as any).isSliding ?? false;

      remote.updateFromServer({
        x: snapshot.position.x,
        y: snapshot.position.y,
        z: snapshot.position.z,
        yaw: snapshot.yaw,
        pitch: snapshot.pitch,
        timestamp: performance.now(),
      });

      // Visibility based on health
      if (remote.mesh) {
        remote.mesh.visible = snapshot.health > 0;
      }
    }

    // Remove players not in this frame
    for (const [id, remote] of this.replayPlayers) {
      if (!seenIds.has(id)) {
        remote.removeMesh(this.scene);
        this.replayPlayers.delete(id);
      }
    }
  }

  private updateSpectatorCamera(frame: ReplayFrame): void {
    // If we have a follow target and the spectator system is active,
    // make sure the target is set
    if (this.followTargetId && this.spectator.active) {
      // Verify target still exists and is alive
      const target = this.replayPlayers.get(this.followTargetId);
      if (!target || target.health <= 0) {
        // Find first alive player
        for (const [id, rp] of this.replayPlayers) {
          if (rp.health > 0) {
            this.followTargetId = id;
            this.spectator.setTarget(id);
            break;
          }
        }
      }
    }
  }
}
